/**
 * STATIC FINANCE STATUS DEFINITIONS (Client-Safe Fallback)
 *
 * Mirrors the finance-domain status_definitions from types.yaml.
 * Used as a fallback when the WorkItemTypeConfigRegistry is not initialized
 * (i.e., when running in a browser/client component where fs and js-yaml are unavailable).
 *
 * Pattern matches financeTypes.ts which statically mirrors finance type keys.
 *
 * KEEP IN SYNC WITH types.yaml status_definitions (domain: finance entries).
 */

import type { StatusDefinition, UserAction } from './types';

// ============================================================================
// FINANCE ITEM TYPES
// ============================================================================

/**
 * All item types that belong to the finance domain (have domain: finance in types.yaml).
 * Includes _generic which is the finance fallback entry.
 */
export const STATIC_FINANCE_ITEM_TYPES = new Set<string>([
  'invoice',
  'quote',
  'quote_request',
  'proforma',
  'credit_note',
  'debit_note',
  'delivery_note',
  'waybill',
  'sales_order',
  'purchase_order',
  'supplier_credit',
  'supplier_quote',
  'grn',
  'rfq',
  'billing_submission',
  'expense',
  'purchase',
  'fuel',
  'toll',
  'maintenance',
  'breakdown',
  'purchase_requisition',
  'supplier_invoice',
  'work_order',
  // Property Finance
  'levy_cycle',
  'meter_reading',
  'utility_recovery',
  'special_levy',
  'scheme_budget',
  'reserve_fund',
  'deposit_holding',
  'trust_transaction',
  'payment_dispute',
  'payment_plan',
  'collection_case',
  'invoice_followup',
  'credit_request',
  'write_off',
  // Quarantined external-sync conflict copies (item_subtype = original doc type)
  'conflict',
  '_generic',
]);

// ============================================================================
// STATUS DEFINITIONS
// ============================================================================

export const STATIC_FINANCE_STATUS_DEFINITIONS = new Map<string, StatusDefinition[]>([
  ['invoice', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'paid', label: 'Paid', color: 'green', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'partial', label: 'Partially Paid', color: 'orange', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'overdue', label: 'Overdue', color: 'red', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['quote', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'viewed', label: 'Viewed', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'accepted', label: 'Accepted', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'declined', label: 'Declined', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'expired', label: 'Expired', color: 'orange', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'converted', label: 'Converted', color: 'purple', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['quote_request', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'responded', label: 'Responded', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['proforma', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'converted', label: 'Converted', color: 'green', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['credit_note', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'applied', label: 'Applied', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['debit_note', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'acknowledged', label: 'Acknowledged', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'applied', label: 'Applied', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['delivery_note', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'delivered', label: 'Delivered', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['waybill', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'in_transit', label: 'In Transit', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'delivered', label: 'Delivered', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'returned', label: 'Returned', color: 'yellow', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['sales_order', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'confirmed', label: 'Confirmed', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['purchase_order', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'accepted', label: 'Accepted', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['supplier_credit', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'applied', label: 'Applied', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['supplier_quote', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'viewed', label: 'Viewed', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'accepted', label: 'Accepted', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'declined', label: 'Declined', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'expired', label: 'Expired', color: 'orange', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['grn', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'received', label: 'Received', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'accepted', label: 'Accepted', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'closed', label: 'Closed', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['rfq', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'responded', label: 'Responded', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'expired', label: 'Expired', color: 'orange', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['billing_submission', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending_approval', label: 'Pending Approval', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'converted', label: 'Converted', color: 'purple', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['expense', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'received', label: 'Received', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'partial', label: 'Partially Paid', color: 'orange', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'overdue', label: 'Overdue', color: 'red', is_system: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['purchase', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['fuel', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['toll', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['maintenance', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['breakdown', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reimbursed', label: 'Reimbursed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['purchase_requisition', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  // Supplier invoice lifecycle statuses (§3.6.1) — covers the full recon →
  // approval → payment → remittance → publish chain. Intake statuses (new,
  // extracting, matching, to_review, ready) are handled by ExpenseInbox and
  // are not duplicated here; FinanceDocumentView shows promoted invoices.
  ['supplier_invoice', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending_reconciliation', label: 'Pending Recon', color: 'indigo', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reconciling', label: 'Reconciling', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reconciled', label: 'Reconciled', color: 'teal', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending_approval', label: 'Pending Approval', color: 'orange', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'scheduled', label: 'Scheduled', color: 'cyan', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'paid', label: 'Paid', color: 'green', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'remitted', label: 'Remitted', color: 'lime', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'published', label: 'Published', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'void', label: 'Void', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['work_order', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'pending_approval', label: 'Pending Approval', color: 'orange', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'to_schedule', label: 'To Schedule', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'scheduled', label: 'Scheduled', color: 'indigo', visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'in_progress', label: 'In Progress', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid', 'kanban_board'] },
    { key: 'completed', label: 'Completed', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'converted', label: 'Converted to Job', color: 'gray', is_system: true, is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  // ── Property Finance ──
  ['levy_cycle', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'scheduled', label: 'Scheduled', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'billing', label: 'Billing', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'billed', label: 'Billed', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reconciling', label: 'Reconciling', color: 'cyan', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'closed', label: 'Closed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['meter_reading', [
    { key: 'pending', label: 'Pending', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'captured', label: 'Captured', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'verified', label: 'Verified', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'billed', label: 'Billed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'disputed', label: 'Disputed', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['utility_recovery', [
    { key: 'period_open', label: 'Period Open', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'readings', label: 'Readings', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'calculated', label: 'Calculated', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'billed', label: 'Billed', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reconciled', label: 'Reconciled', color: 'cyan', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'closed', label: 'Closed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['special_levy', [
    { key: 'proposed', label: 'Proposed', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'levied', label: 'Levied', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'collecting', label: 'Collecting', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'closed', label: 'Closed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['scheme_budget', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'circulated', label: 'Circulated', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'adopted', label: 'Adopted', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'archived', label: 'Archived', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['reserve_fund', [
    { key: 'planning', label: 'Planning', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'active', label: 'Active', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'funded', label: 'Funded', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'archived', label: 'Archived', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['deposit_holding', [
    { key: 'pending_receipt', label: 'Pending Receipt', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'held', label: 'Held in Trust', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'refund_pending', label: 'Refund Pending', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'refunded', label: 'Refunded', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'forfeited', label: 'Forfeited', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'disputed', label: 'Disputed', color: 'orange', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['trust_transaction', [
    { key: 'pending', label: 'Pending', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'posted', label: 'Posted', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reconciled', label: 'Reconciled', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'reversed', label: 'Reversed', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['payment_dispute', [
    { key: 'submitted', label: 'Submitted', color: 'red', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'investigating', label: 'Investigating', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'customer_response', label: 'Awaiting Customer', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'escalated', label: 'Escalated', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'resolved_accepted', label: 'Resolved - Accepted', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'resolved_rejected', label: 'Resolved - Rejected', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['payment_plan', [
    { key: 'proposed', label: 'Proposed', color: 'blue', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'active', label: 'Active', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'missed_payment', label: 'Missed Payment', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'completed', label: 'Completed', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'defaulted', label: 'Defaulted', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['collection_case', [
    { key: 'new', label: 'New Case', color: 'blue', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'first_contact', label: 'First Contact', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'negotiating', label: 'Negotiating', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'payment_arrangement', label: 'Payment Arrangement', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'legal_action', label: 'Legal Action', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'recovered', label: 'Recovered', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'write_off', label: 'Written Off', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['invoice_followup', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'sent', label: 'Sent', color: 'blue', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'outstanding', label: 'Outstanding', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'overdue', label: 'Overdue', color: 'orange', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'dispute', label: 'In Dispute', color: 'red', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'payment_pending', label: 'Payment Pending', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'paid', label: 'Paid', color: 'green', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'written_off', label: 'Written Off', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['credit_request', [
    { key: 'requested', label: 'Requested', color: 'blue', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending_approval', label: 'Pending Approval', color: 'amber', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'issued', label: 'Credit Issued', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['write_off', [
    { key: 'requested', label: 'Requested', color: 'amber', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending_approval', label: 'Pending Approval', color: 'purple', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'rejected', label: 'Rejected', color: 'red', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'processed', label: 'Processed', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  // Sync-conflict copies only ever sit in conflict_review — resolution
  // soft-deletes the row, so there are no terminal statuses.
  ['conflict', [
    { key: 'conflict_review', label: 'Conflict Review', color: 'amber', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
  ['_generic', [
    { key: 'draft', label: 'Draft', color: 'gray', is_initial: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'pending', label: 'Pending', color: 'yellow', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'approved', label: 'Approved', color: 'green', visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'closed', label: 'Closed', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
    { key: 'cancelled', label: 'Cancelled', color: 'gray', is_terminal: true, visible_in: ['smart_task', 'admin_sidebar', 'data_grid'] },
  ]],
]);

// ============================================================================
// TERMINAL STATUSES
// ============================================================================

function buildTerminalStatusesMap(): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const [itemType, statuses] of STATIC_FINANCE_STATUS_DEFINITIONS) {
    const terminal = new Set<string>();
    for (const s of statuses) {
      if (s.is_terminal) terminal.add(s.key);
    }
    map.set(itemType, terminal);
  }
  return map;
}

export const STATIC_FINANCE_TERMINAL_STATUSES = buildTerminalStatusesMap();

// ============================================================================
// USER ACTIONS
// ============================================================================

export const STATIC_FINANCE_USER_ACTIONS = new Map<string, Record<string, UserAction[]>>([
  ['invoice', {
    draft: [{ id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this draft invoice?' }],
    sent: [
      { id: 'resend', label: 'Resend', target_status: 'sent', tone: 'neutral' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this invoice? This cannot be undone.' },
    ],
    partial: [{ id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void a partially-paid invoice? Payments must be unmatched first.' }],
    overdue: [
      { id: 'resend', label: 'Send reminder', target_status: 'sent', tone: 'warning' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
  }],
  ['quote', {
    draft: [
      { id: 'send', label: 'Send', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'decline', label: 'Mark declined', target_status: 'declined', tone: 'danger' },
      { id: 'resend', label: 'Resend', target_status: 'sent', tone: 'neutral' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    accepted: [{ id: 'reopen', label: 'Re-open as draft', target_status: 'draft', tone: 'neutral' }],
    declined: [{ id: 'reopen', label: 'Re-open as draft', target_status: 'draft', tone: 'neutral' }],
  }],
  ['quote_request', {
    draft: [
      { id: 'send', label: 'Send', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'decline', label: 'Mark declined', target_status: 'declined', tone: 'danger' },
      { id: 'resend', label: 'Resend', target_status: 'sent', tone: 'neutral' },
    ],
    declined: [{ id: 'reopen', label: 'Re-open as draft', target_status: 'draft', tone: 'neutral' }],
  }],
  ['proforma', {
    draft: [
      { id: 'send', label: 'Send', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'decline', label: 'Mark declined', target_status: 'declined', tone: 'danger' },
      { id: 'resend', label: 'Resend', target_status: 'sent', tone: 'neutral' },
    ],
  }],
  ['credit_note', {
    draft: [
      { id: 'issue', label: 'Issue', target_status: 'sent', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    sent: [{ id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this credit note?' }],
    applied: [],
  }],
  ['debit_note', {
    draft: [
      { id: 'issue', label: 'Issue', target_status: 'sent', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    sent: [{ id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this debit note?' }],
    acknowledged: [],
    applied: [],
  }],
  ['delivery_note', {
    draft: [
      { id: 'issue', label: 'Issue', target_status: 'sent', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    sent: [{ id: 'mark_delivered', label: 'Mark Delivered', target_status: 'delivered', tone: 'success' }],
    delivered: [],
  }],
  ['waybill', {
    draft: [
      { id: 'dispatch', label: 'Dispatch', target_status: 'in_transit', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    in_transit: [
      { id: 'mark_delivered', label: 'Mark Delivered', target_status: 'delivered', tone: 'success' },
      { id: 'mark_returned', label: 'Mark Returned', target_status: 'returned', tone: 'warning' },
    ],
    delivered: [],
    returned: [],
  }],
  ['sales_order', {
    draft: [
      { id: 'confirm', label: 'Confirm', target_status: 'confirmed', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    confirmed: [{ id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' }],
  }],
  ['purchase_order', {
    draft: [
      { id: 'send', label: 'Send to supplier', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    accepted: [{ id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' }],
  }],
  ['supplier_credit', {
    draft: [
      { id: 'issue', label: 'Issue', target_status: 'sent', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    sent: [{ id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this supplier credit?' }],
    applied: [],
  }],
  ['supplier_quote', {
    draft: [
      { id: 'send', label: 'Send', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'decline', label: 'Mark declined', target_status: 'declined', tone: 'danger' },
      { id: 'resend', label: 'Resend', target_status: 'sent', tone: 'neutral' },
    ],
  }],
  ['grn', {
    draft: [
      { id: 'receive', label: 'Mark Received', target_status: 'received', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    received: [{ id: 'accept', label: 'Accept', target_status: 'accepted', tone: 'success' }],
    accepted: [{ id: 'close', label: 'Close', target_status: 'closed', tone: 'neutral' }],
    closed: [],
  }],
  ['rfq', {
    draft: [
      { id: 'send', label: 'Send to supplier', target_status: 'sent', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    sent: [
      { id: 'accept', label: 'Mark accepted', target_status: 'accepted', tone: 'success' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
  }],
  ['billing_submission', {
    draft: [
      { id: 'submit', label: 'Submit for Approval', target_status: 'pending_approval', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending_approval: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this billing submission?' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this billing submission to operations?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this billing submission?' },
    ],
    approved: [
      { id: 'generate_invoice', label: 'Generate Invoice', target_status: 'converted', tone: 'primary' },
      { id: 'generate_quote', label: 'Generate Quote', target_status: 'converted', tone: 'primary' },
      { id: 'generate_proforma', label: 'Generate Proforma', target_status: 'converted', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this billing submission to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending_approval', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    converted: [],
    void: [],
  }],
  ['expense', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['purchase', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['fuel', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this fuel expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this fuel expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this fuel expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this fuel expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['toll', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this toll expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this toll expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this toll expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this toll expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['maintenance', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this maintenance expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this maintenance expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this maintenance expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this maintenance expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['breakdown', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success', confirm: 'Approve this breakdown expense?' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning', confirm: 'Reject this breakdown expense?' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this breakdown expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', target_status: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', target_status: 'rejected', tone: 'warning', confirm: 'Return this breakdown expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', target_status: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reimbursed: [],
    void: [],
  }],
  ['purchase_requisition', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success' },
      { id: 'reject', label: 'Reject', target_status: 'rejected', tone: 'warning' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
  }],
  // Supplier invoice lifecycle user actions (§3.6.1)
  ['supplier_invoice', {
    draft: [
      { id: 'send_to_recon', label: 'Send to Reconciliation', target_status: 'pending_reconciliation', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger', confirm: 'Void this supplier invoice?' },
    ],
    pending_reconciliation: [
      { id: 'start_recon', label: 'Start Reconciliation', target_status: 'reconciling', tone: 'primary' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reconciling: [
      { id: 'complete_recon', label: 'Mark Reconciled', target_status: 'reconciled', tone: 'success' },
      { id: 'void', label: 'Void', target_status: 'void', tone: 'danger' },
    ],
    reconciled: [
      { id: 'submit_for_approval', label: 'Submit for Payment Approval', target_status: 'pending_approval', tone: 'primary' },
      { id: 'reopen_recon', label: 'Reopen Reconciliation', target_status: 'reconciling', tone: 'warning' },
    ],
    pending_approval: [
      { id: 'approve', label: 'Approve Payment', target_status: 'approved', tone: 'success' },
      { id: 'reject', label: 'Reject', target_status: 'reconciled', tone: 'warning' },
    ],
    approved: [
      { id: 'schedule', label: 'Schedule Payment', target_status: 'scheduled', tone: 'primary' },
    ],
    scheduled: [
      { id: 'mark_paid', label: 'Mark Paid', target_status: 'paid', tone: 'success' },
    ],
    paid: [
      { id: 'mark_remitted', label: 'Mark Remittance Sent', target_status: 'remitted', tone: 'neutral' },
    ],
    remitted: [
      { id: 'mark_published', label: 'Mark Published', target_status: 'published', tone: 'neutral' },
    ],
    published: [],
    void: [],
  }],
  ['work_order', {
    draft: [
      { id: 'submit', label: 'Submit for Scheduling', target_status: 'to_schedule', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    pending_approval: [
      { id: 'revert', label: 'Return to Draft', target_status: 'draft', tone: 'neutral' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    rejected: [
      { id: 'reopen', label: 'Re-open as Draft', target_status: 'draft', tone: 'neutral' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    to_schedule: [
      { id: 'schedule', label: 'Schedule', target_status: 'scheduled', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    scheduled: [
      { id: 'start', label: 'Start Work', target_status: 'in_progress', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    in_progress: [
      { id: 'complete', label: 'Mark Completed', target_status: 'completed', tone: 'success' },
    ],
    completed: [],
    converted: [],
    cancelled: [],
  }],
  ['_generic', {
    draft: [
      { id: 'submit', label: 'Submit', target_status: 'pending', tone: 'primary' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', target_status: 'approved', tone: 'success' },
      { id: 'cancel', label: 'Cancel', target_status: 'cancelled', tone: 'danger' },
    ],
    approved: [{ id: 'close', label: 'Close', target_status: 'closed', tone: 'neutral' }],
  }],
]);

// ============================================================================
// HELPER FUNCTIONS (Static Fallback)
// ============================================================================

/**
 * Get finance statuses for an item type from static definitions.
 * Falls back to _generic if the specific type is not found.
 */
export function getStaticFinanceStatuses(itemType: string): StatusDefinition[] {
  return STATIC_FINANCE_STATUS_DEFINITIONS.get(itemType)
    || STATIC_FINANCE_STATUS_DEFINITIONS.get('_generic')
    || [];
}

/**
 * Get a specific status definition by item type and status key from static definitions.
 */
export function getStaticStatusDefinition(itemType: string, statusKey: string): StatusDefinition | null {
  const statuses = getStaticFinanceStatuses(itemType);
  return statuses.find(s => s.key === statusKey) || null;
}

/**
 * Check if an item type is a finance type using static definitions.
 */
export function isStaticFinanceItemType(itemType: string): boolean {
  return STATIC_FINANCE_ITEM_TYPES.has(itemType);
}

/**
 * Check if a status is terminal for an item type using static definitions.
 */
export function isStaticTerminalStatus(itemType: string, statusKey: string): boolean {
  const termSet = STATIC_FINANCE_TERMINAL_STATUSES.get(itemType)
    || STATIC_FINANCE_TERMINAL_STATUSES.get('_generic');
  return termSet ? termSet.has(statusKey) : false;
}

/**
 * Check if a status is system-managed for an item type using static definitions.
 */
export function isStaticSystemStatus(itemType: string, statusKey: string): boolean {
  const def = getStaticStatusDefinition(itemType, statusKey);
  return def?.is_system ?? false;
}

/**
 * Get user actions for an item type and current status using static definitions.
 */
export function getStaticUserActions(itemType: string, currentStatus: string): UserAction[] {
  const actions = STATIC_FINANCE_USER_ACTIONS.get(itemType)
    || STATIC_FINANCE_USER_ACTIONS.get('_generic')
    || {};
  return actions[currentStatus] || [];
}

/**
 * Get allowed transitions from a current status using static definitions.
 */
export function getStaticAllowedTransitions(itemType: string, currentStatus: string): string[] {
  const statuses = getStaticFinanceStatuses(itemType);
  const allowed = new Set<string>();

  for (const s of statuses) {
    if (s.allowed_from?.includes(currentStatus)) {
      allowed.add(s.key);
    }
  }

  const actions = getStaticUserActions(itemType, currentStatus);
  for (const a of actions) {
    allowed.add(a.target_status);
  }

  return Array.from(allowed);
}

/**
 * Get the color for a status key of an item type using static definitions.
 */
export function getStaticStatusColor(itemType: string, statusKey: string): string | null {
  const def = getStaticStatusDefinition(itemType, statusKey);
  return def?.color || null;
}

/**
 * Get the label for a status key of an item type using static definitions.
 */
export function getStaticStatusLabel(itemType: string, statusKey: string): string | null {
  const def = getStaticStatusDefinition(itemType, statusKey);
  return def?.label || null;
}

/**
 * Get data-grid visible statuses using static definitions.
 */
export function getStaticDataGridVisibleStatuses(itemType: string): StatusDefinition[] {
  return getStaticFinanceStatuses(itemType).filter(
    s => !s.visible_in || s.visible_in.includes('data_grid')
  );
}

/**
 * Get admin-sidebar visible statuses using static definitions.
 */
export function getStaticAdminSidebarVisibleStatuses(itemType: string): StatusDefinition[] {
  return getStaticFinanceStatuses(itemType).filter(
    s => !s.visible_in || s.visible_in.includes('admin_sidebar')
  );
}
